import React from "react";
import { ColumnsType } from "antd/es/table";
import { TFunction } from "i18next";

import IconCheck from "~/components/Icons/icon-check";
import { getStaffCellToneClass } from "~/components/Table/TableRow/StaffTableRow/staffCellCompare";
import { useGetLocationData } from "~/hooks/locationData";
import { getFormatDate, getShortDate } from "~/libs/getFormatDate";
import { TDealerMetric } from "~/pages/Staff/ui/StaffFilter/model/config";
import { ISelectOption } from "~/shared/ui/Fields/types";
import { InfoTooltip } from "~/shared/ui/Tooltip";

import s from "./tableHeaderStyle.less";

const renderValue = (value: any) => <span>{value}</span>;

export const getStaffColumns = (
  t: TFunction,
  options: string[],
): ColumnsType<any> =>
  options.map((option) => ({
    title: t(`staff.${option}`),
    dataIndex: option,
    key: option,
    render: renderValue,
  }));

export const getSpecStaffColumns = (
  t: TFunction,
  options: Array<string | { key: string; handle: () => string }>,
  maxFromDate: Date | string,
  dealerMetric?: TDealerMetric,
  currentDate?: any,
): ColumnsType<any> => {
  const { filterBy, filterItems, period } = useGetLocationData() as any;

  const columns: ColumnsType<any> = [];

  const processedOptions = options.map((option) => {
    const isObject = typeof option === "object" && "key" in option;
    const key = isObject ? option.key : (option as string);

    if (key === "futureNumber") {
      const fromDateParam = maxFromDate
        ? `${getFormatDate(maxFromDate, true)},`
        : "";
      return {
        key,
        title: t("staff.futureNumber", { fromDate: fromDateParam }),
      };
    }
    if (key === "standardNumber") {
      return {
        key,
        title: t("staff.standardNumber", {
          date: currentDate ? getFormatDate(currentDate) : "",
        }),
      };
    }

    console.log(maxFromDate, "maxFromDate");
    console.log(currentDate, "currentDate");

    const title = isObject ? option.handle() : t(`staff.${option}`);
    return { key, title };
  });

  const toneOnCell = (key: string) => (record: Record<string, unknown>) => {
    const tone = getStaffCellToneClass(record, key);
    return tone ? { className: tone } : {};
  };

  const renderDealerMetricTitle = (metric?: TDealerMetric["future"]) =>
    metric ? (
      <InfoTooltip
        title={
          <div>
            {t("staff.posts")} – {metric.posts} <br />
            {t("staff.plannedSalesNextYear")} – {metric.plannedSalesNextYear}{" "}
            <br />
            {t("staff.forecastServiceVisits")} – {metric.forecastServiceVisits}
          </div>
        }
      >
        <span className={s.cat}>
          {t("staff.category")} {metric.category}
        </span>
      </InfoTooltip>
    ) : null;

  const getRequirementPair = (
    title: string,
    personOption: { key: string; title: string },
    percentOption: { key: string; title: string },
    borderClass: string,
    tonePercent: boolean,
  ): ColumnsType<any>[number] => ({
    title,
    className: borderClass,
    children: [
      {
        className: borderClass,
        onHeaderCell: () => ({ className: borderClass }),
        title: t("staff.personRequired"),
        dataIndex: personOption.key,
        key: personOption.key,
        render: renderValue,
      },
      {
        title: "%",
        dataIndex: percentOption.key,
        key: percentOption.key,
        render: renderValue,
        ...(tonePercent ? { onCell: toneOnCell(percentOption.key) } : {}),
      },
    ],
  });

  // Первые 3 колонки: kpi, code, position (без группировки)
  columns.push(
    {
      title: processedOptions[0].title,
      dataIndex: processedOptions[0].key,
      key: processedOptions[0].key,

      render: (value: any) => {
        return value ? <IconCheck fill={"#495A64"} /> : <></>;
      },
    },
    {
      title: processedOptions[1].title,
      dataIndex: processedOptions[1].key,
      key: processedOptions[1].key,
      render: renderValue,
    },
    {
      title: processedOptions[2].title,
      dataIndex: processedOptions[2].key,
      key: processedOptions[2].key,
      render: (value: any, record) => {
        const shortPeriod = getShortDate(
          period.toString().includes("T")
            ? period.toString().split("T")[0]
            : period,
        );
        const positionIds = (record.Positions || []).map(
          (p: { instanceId: number }) => p.instanceId,
        );
        const paramString = new URLSearchParams({
          position: value,
          positionId: JSON.stringify(positionIds),
          filterBy: filterBy.value,
          filterItems: JSON.stringify(
            (filterItems || []).map(
              (el: ISelectOption) => `${el.value}, ${el.label}`,
            ),
          ),
          period: shortPeriod,
        });

        return (
          <a
            className={s.personalLink}
            onClick={() =>
              window.open(`/staff/employee?${paramString.toString()}`, "_blank")
            }
          >
            {value}
          </a>
        );
      },
    },
  );

  // Группа "Численность" (futureNumber, standardNumber, factNumber)
  columns.push({
    title: t("staff.count"),
    className: s.groupSegmentBorder,
    children: [
      {
        className: s.groupSegmentBorder,
        title: renderDealerMetricTitle(dealerMetric?.future),
        children: [
          {
            className: s.groupSegmentBorder,
            onHeaderCell: () => ({ className: s.groupSegmentBorder }),
            title: processedOptions[3].title,
            dataIndex: processedOptions[3].key,
            key: processedOptions[3].key,
            render: renderValue,
          },
        ],
      },
      {
        title: renderDealerMetricTitle(dealerMetric?.rightNow),
        children: [
          {
            title: processedOptions[4].title,
            dataIndex: processedOptions[4].key,
            key: processedOptions[4].key,
            render: renderValue,
          },
        ],
      },
      {
        className: s.groupSegmentBorder,
        onHeaderCell: () => ({ className: s.groupSegmentBorder }),
        title: processedOptions[5].title,
        dataIndex: processedOptions[5].key,
        key: processedOptions[5].key,
        render: renderValue,
        onCell: toneOnCell(processedOptions[5].key),
      },
    ],
  });

  // Группа "Обучение" с подгруппами "Требуется" и "Обучено"
  columns.push({
    title: t("staff.training"),
    className: s.groupSegmentBorder,
    children: [
      getRequirementPair(
        t("staff.requirement"),
        processedOptions[6],
        processedOptions[7],
        s.groupSegmentBorder,
        false,
      ),
      getRequirementPair(
        t("staff.trained"),
        processedOptions[8],
        processedOptions[9],
        s.subgroupSegmentBorder,
        true,
      ),
    ],
  });

  // Группа "Сертификация" с подгруппами "Требуется" и "Сертифицировано"
  columns.push({
    title: t("staff.certification"),
    className: s.groupSegmentBorder,
    children: [
      getRequirementPair(
        t("staff.requirement"),
        processedOptions[10],
        processedOptions[11],
        s.groupSegmentBorder,
        false,
      ),
      getRequirementPair(
        t("staff.certified"),
        processedOptions[12],
        processedOptions[13],
        s.subgroupSegmentBorder,
        true,
      ),
    ],
  });

  if (processedOptions[14] && processedOptions[15]) {
    columns.push({
      title: t("staff.licenses"),
      className: s.groupSegmentBorder,
      children: [
        {
          className: s.groupSegmentBorder,
          onHeaderCell: () => ({ className: s.groupSegmentBorder }),
          title: processedOptions[14].title,
          dataIndex: processedOptions[14].key,
          key: processedOptions[14].key,
          render: renderValue,
        },
        {
          title: processedOptions[15].title,
          dataIndex: processedOptions[15].key,
          key: processedOptions[15].key,
          render: renderValue,
        },
      ],
    });
  }

  return columns;
};
